import React, { useState, useEffect } from "react";
import { setPageMeta, shareLink } from "@/lib/pageMeta";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, CalendarDays, CheckCircle2, AlertCircle, Video, Tag, Share2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CampaignJoin() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [error, setError] = useState(null);
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/campaigns/${slug}`).then(async r => { const d = await r.json(); if (r.ok) { setC(d); setPageMeta({ title: `${d.title} | PlateMind`, description: [d.when, d.description].filter(Boolean).join(". ").slice(0, 180), image: d.banner_url || "/og-default.jpg" }); } else setError(d.detail); }).catch(() => setError("Connection problem."));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) { toast.error("Please enter your name and email."); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/public/campaigns/${slug}/register`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(f) });
      const d = await r.json();
      if (r.ok) setDone(true);
      else toast.error(typeof d.detail === "string" ? d.detail : d.detail?.[0]?.msg?.replace("Value error, ", "") || "Could not register.");
    } catch { toast.error("Connection problem."); } finally { setSaving(false); }
  };

  const share = async () => {
    const url = shareLink(`/event/${slug}`);
    const text = `${c.title}${c.when ? ` · ${c.when}` : ""}`;
    if (navigator.share) { try { await navigator.share({ title: c.title, text, url }); return; } catch { return; } }
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`, "_blank");
  };

  if (error) return <div className="min-h-screen bg-[#FAFAF9] py-12 px-4"><Card className="max-w-md mx-auto rounded-2xl p-8 text-center" data-testid="join-error"><AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" /><p className="text-sm text-stone-600">{error}</p></Card></div>;
  if (!c) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF7A00]" /></div>;
  const Icon = c.campaign_type === "webinar" ? Video : c.campaign_type === "event" ? CalendarDays : Tag;

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="campaign-join-page">
      <div className="max-w-xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <img src="/platemind-logo-new.png" alt="PlateMind Nutrition" className="h-9 w-auto" />
          <Button variant="outline" size="sm" onClick={share} className="rounded-full text-xs" data-testid="join-share-btn"><Share2 className="w-3.5 h-3.5 mr-1.5" />Share</Button>
        </div>
        {c.banner_url && <img src={c.banner_url} alt="" className="w-full rounded-2xl object-cover max-h-72 shadow-sm" data-testid="join-banner" />}
        <div>
          <p className="text-xs font-bold text-[#FF7A00] uppercase tracking-widest flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" />{c.campaign_type === "promotion" ? "Special offer" : `Free ${c.campaign_type}`}</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight mt-2 leading-tight" data-testid="join-title">{c.title}</h1>
          {c.when && <p className="text-sm font-semibold text-stone-700 mt-3 flex items-center gap-2" data-testid="join-when"><CalendarDays className="w-4 h-4 text-[#FF7A00]" />{c.when}</p>}
          {c.description && <p className="text-sm text-stone-600 mt-3 whitespace-pre-line leading-relaxed">{c.description}</p>}
        </div>
        {done ? (
          <Card className="rounded-2xl p-6 bg-green-50 border-green-200 text-center" data-testid="join-success">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-2" />
            <p className="text-lg font-bold text-green-900">You're registered!</p>
            <p className="text-sm text-green-800 mt-1">We've sent the details to {f.email}. Check your inbox (and spam folder).</p>
          </Card>
        ) : !c.active ? (
          <Card className="rounded-2xl p-6 text-center text-sm text-stone-600" data-testid="join-closed">Registration for this {c.campaign_type} has closed.</Card>
        ) : (
          <Card className="rounded-2xl p-5 sm:p-6 border-stone-200 shadow-sm">
            <form onSubmit={submit} className="space-y-3" data-testid="join-form">
              <p className="text-sm font-bold text-stone-900">Reserve your spot</p>
              <Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Full name" className="rounded-xl h-11" data-testid="join-name" />
              <Input type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} placeholder="Email address" className="rounded-xl h-11" data-testid="join-email" />
              <Input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} placeholder="WhatsApp number, e.g. +234 801 234 5678" className="rounded-xl h-11" data-testid="join-phone" />
              <p className="text-[11px] text-stone-500 leading-relaxed" data-testid="join-email-notice">
                By registering, you'll receive emails about this {c.campaign_type === "promotion" ? "offer" : c.campaign_type}, including reminders and follow-ups, plus occasional nutrition tips from PlateMind Nutrition. You can unsubscribe at any time.
              </p>
              <Button type="submit" disabled={saving} className="w-full bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-xl h-12 font-bold" data-testid="join-submit">
                {saving && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}Register now
              </Button>
              <p className="text-[11px] text-stone-400 text-center">We'll email you about this {c.campaign_type}. See our <a href="/privacy" className="underline">Privacy Policy</a>.</p>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
